import { useState } from 'react';
import { Box, ToggleButton, ToggleButtonGroup, Typography, Button, useTheme } from '@mui/material';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import { getChartColors } from '../utils/chartConfig';
import FullComparisonModal from './FullComparisonModal';

/**
 * PaymentToggle - Switch between Upfront and 3-Year payment views
 *
 * @param {string} value - Current payment mode ('upfront' or 'threeYear')
 * @param {function} onChange - Callback when mode changes
 * @param {Object} upfront - Upfront payment metrics (for comparison modal)
 * @param {Object} threeYear - 3-Year payment metrics (for comparison modal)
 * @param {Object} portfolioData - Full portfolio data for comprehensive comparison
 * @param {Object} cfoMetrics - CFO metrics for financial impact analysis
 * @param {Object} companyConfig - Company configuration for calculations
 * @param {Object} roiResults - Full ROI results for equipment breakdown charts
 */
export default function PaymentToggle({ value, onChange, upfront, threeYear, portfolioData, cfoMetrics, companyConfig, roiResults }) {
  const theme = useTheme();
  const colors = getChartColors(theme.palette.mode);
  const [compareOpen, setCompareOpen] = useState(false);

  const handleChange = (event, newValue) => {
    if (newValue !== null) {
      onChange(newValue);
    }
  };

  const canCompare = upfront && threeYear && portfolioData;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { xs: 'stretch', sm: 'center' },
        gap: { xs: 1.5, sm: 2 },
        mb: 3,
        p: 2,
        borderRadius: 2,
        background: (theme) => theme.palette.mode === 'dark' ? 'rgba(26, 31, 37, 0.6)' : 'rgba(248, 249, 250, 0.9)',
        border: (theme) => `1px solid ${theme.palette.divider}`,
        overflow: 'hidden',
        maxWidth: '100%',
      }}
    >
      {/* Label - hidden on mobile */}
      <Typography
        variant="body2"
        sx={{
          display: { xs: 'none', sm: 'block' },
          fontWeight: 600,
          color: 'text.secondary',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          fontSize: '0.75rem',
          flexShrink: 0,
        }}
      >
        Payment Option:
      </Typography>

      {/* Toggle buttons row */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: { xs: 1, sm: 2 },
          width: { xs: '100%', sm: 'auto' },
          flexWrap: 'wrap',
        }}
      >
        <ToggleButtonGroup
          value={value}
          exclusive
          onChange={handleChange}
          size="small"
          sx={{
            width: { xs: '100%', sm: 'auto' },
            flexShrink: 0,
            '& .MuiToggleButton-root': {
              color: 'text.secondary',
              borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.15)',
              textTransform: 'none',
              fontWeight: 600,
              px: { xs: 2, sm: 3 },
              py: 0.75,
              fontSize: '0.875rem',
              transition: 'all 0.2s ease',
              whiteSpace: 'nowrap',

              '&.Mui-selected': {
                background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryLight} 100%)`,
                color: 'primary.contrastText',
                borderColor: colors.primary,

                '&:hover': {
                  background: `linear-gradient(135deg, ${colors.primaryLight} 0%, ${colors.primary} 100%)`,
                },
              },

              '&:hover': {
                background: 'rgba(160, 206, 78, 0.1)',
                borderColor: 'rgba(160, 206, 78, 0.3)',
              },
            },
          }}
        >
          <ToggleButton value="upfront">
            Upfront
          </ToggleButton>
          <ToggleButton value="threeYear">
            3-Year
          </ToggleButton>
        </ToggleButtonGroup>

        {canCompare && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<CompareArrowsIcon />}
            onClick={() => setCompareOpen(true)}
            sx={{
              width: { xs: '100%', sm: 'auto' },
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.8rem',
              color: theme.palette.mode === 'dark' ? colors.teal : 'text.primary',
              borderColor: 'rgba(78, 205, 196, 0.4)',
              whiteSpace: 'nowrap',
              flexShrink: 0,
              '&:hover': {
                borderColor: colors.teal,
                background: 'rgba(78, 205, 196, 0.1)',
              },
            }}
          >
            Compare
          </Button>
        )}
      </Box>

      {/* Help text - hidden on mobile */}
      <Typography
        variant="caption"
        sx={{
          display: { xs: 'none', md: 'block' },
          color: 'text.secondary',
          fontStyle: 'italic',
          ml: 'auto',
          flexShrink: 0,
        }}
      >
        Same total cost &amp; 5-year savings
      </Typography>

      {/* Full Comparison Modal */}
      <FullComparisonModal
        open={compareOpen}
        onClose={() => setCompareOpen(false)}
        portfolioData={portfolioData}
        paymentMetrics={{ upfront, threeYear }}
        activeMode={value}
        cfoMetrics={cfoMetrics}
        companyConfig={companyConfig}
        roiResults={roiResults}
      />
    </Box>
  );
}
